import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { DEFAULT_CLIDS } from '@/constants/general'
import { CLIENT_QUERY, SSR_QUERY } from '@/router/query-params'
import { useAppStore } from '@/store/app-store'
import type { APIGameType } from '@/types'
import safeURLParse from '@/utils/safeURLParse'

const GAME_IFRAME_QUERY_PARAMS_BLACKLIST: Set<string> = new Set([
    CLIENT_QUERY.GAME_PLATFORM,
    CLIENT_QUERY.GAME_PAYLOAD,
    CLIENT_QUERY.GAME_DISTRIBUTION_GAME_ID,
])

export function useGameIframeUrl() {
    const route = useRoute()
    const appStore = useAppStore()
    const isMounted = ref(false)

    onMounted(() => {
        isMounted.value = true
    })

    function buildGameIframeUrl(game: APIGameType): URL | null {
        if (!isMounted.value) {
            return null
        }

        const url = safeURLParse(game.game_url)

        if (!url) {
            return null
        }

        if (url.hostname.endsWith('games.playgama.com')) {
            url.hostname = url.hostname.replace('games.playgama.com', 'games.playgama.net')
        }

        if (appStore.clid && !Object.values(DEFAULT_CLIDS).includes(appStore.clid)) {
            url.searchParams.set(SSR_QUERY.CLID, appStore.clid as string)
        }

        if (game.source === 'playgama') {
            url.searchParams.set(CLIENT_QUERY.GAME_ID, game.id)

            // for games hosted on 3d party servers (like "hero wars") do pass all query params
            // it helps to segment audience on those games
            if (!url.hostname.endsWith('games.playgama.net')) {
                for (const [key, value] of Object.entries(route.query)) {
                    if (GAME_IFRAME_QUERY_PARAMS_BLACKLIST.has(key)) {
                        continue
                    }

                    const strValue = typeof value === 'string' ? value : ''
                    if (strValue) {
                        url.searchParams.set(key, strValue)
                    }
                }
            }
        }

        return url
    }

    return { buildGameIframeUrl }
}
